import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, CheckCircle2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact upload widget.
 *   - Short `label` on the button (icon LEFT inside the button, text truncated
 *     so it never overflows a narrow column).
 *   - Optional `hint` shown below the button in small gray text (details/rules).
 *   - After a successful upload, the button is replaced by an "uploaded" row:
 *     green tick + filename + "Cambiar" button that re-opens the file picker.
 *   - `preview` (data URL) shows a small image thumbnail alongside the row.
 */
export default function FileUpload({ label, hint, accept, maxMB, onData, preview, currentFilename, testid, changeLabel = "Cambiar" }) {
  const inputRef = useRef();
  const [previewUrl, setPreviewUrl] = useState(preview || "");
  const [filename, setFilename] = useState(currentFilename || "");

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxMB * 1024 * 1024) {
      toast.error(`Archivo demasiado grande (máx ${maxMB} MB)`);
      e.target.value = ""; return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreviewUrl(dataUrl);
      setFilename(f.name);
      onData(dataUrl, f.name);
    };
    reader.readAsDataURL(f);
    e.target.value = ""; // allow re-picking the same file
  };

  const hasUpload = !!(previewUrl || filename);
  const isImage = previewUrl && previewUrl.startsWith("data:image");

  return (
    <div className="space-y-1.5">
      <input ref={inputRef} type="file" accept={accept} onChange={handleFile}
             className="hidden" data-testid={`${testid}-input`}/>

      {!hasUpload && (
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}
                data-testid={`${testid}-btn`}
                className="w-full h-10 justify-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50">
          <Upload size={14} className="shrink-0"/>
          <span className="truncate text-sm font-medium">{label}</span>
        </Button>
      )}

      {hasUpload && (
        <div data-testid={`${testid}-uploaded`}
             className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5">
          <div className="flex items-center gap-2 min-w-0">
            {isImage ? (
              <img src={previewUrl} alt="preview" data-testid={`${testid}-thumb`}
                   className="h-9 w-9 rounded object-cover border border-emerald-200 shrink-0"/>
            ) : (
              <CheckCircle2 size={16} className="text-emerald-600 shrink-0"/>
            )}
            <span className="text-xs text-emerald-800 font-medium truncate" title={filename}>
              {filename || "Archivo cargado"}
            </span>
          </div>
          <Button type="button" size="sm" variant="ghost"
                  onClick={() => inputRef.current?.click()}
                  data-testid={`${testid}-change-btn`}
                  className="h-7 px-2 text-xs text-emerald-800 hover:bg-emerald-100 shrink-0">
            <RefreshCcw size={12} className="mr-1"/> {changeLabel}
          </Button>
        </div>
      )}

      {hint && <p data-testid={`${testid}-hint`} className="text-[11px] text-slate-500 leading-tight">{hint}</p>}
    </div>
  );
}
